<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import type {
  Project,
  ProjectSpending,
  ProjectSpendingCapPayload
} from '@/stores/type/project.type'

const props = defineProps<{
  project: Project
  spending: ProjectSpending | null
  loading?: boolean
  writeLoading?: boolean
}>()

const emit = defineEmits<{
  'update-cap': [value: ProjectSpendingCapPayload]
}>()

const computeCapInput = ref<number | null>(null)
const storageCapInput = ref<number | null>(null)

const currency = computed(() => props.spending?.currency_code ?? 'EUR')
const computeCost = computed(() => props.spending?.compute_estimated_cost ?? 0)
const storageCost = computed(() => props.spending?.storage_estimated_monthly_cost ?? 0)
const computeCap = computed(() =>
  props.spending?.compute_spending_cap ?? Number(props.project.compute_spending_cap_eur ?? 50)
)
const storageCap = computed(() =>
  props.spending?.storage_spending_cap ?? Number(props.project.storage_spending_cap_eur ?? 50)
)
const computeUsagePercent = computed(() => props.spending?.compute_usage_percent ?? 0)
const storageUsagePercent = computed(() => props.spending?.storage_usage_percent ?? 0)
const computeProgressValue = computed(() => progressValue(computeUsagePercent.value))
const storageProgressValue = computed(() => progressValue(storageUsagePercent.value))
const computeOverLimit = computed(() => Boolean(props.spending?.compute_limit_exceeded))
const storageOverLimit = computed(() => Boolean(props.spending?.storage_limit_exceeded))
const canIncreaseCap = computed(() => Boolean(props.project.can_increase_spending_cap))
const computeCapChanged = computed(
  () => computeCapInput.value !== null && computeCapInput.value !== computeCap.value
)
const storageCapChanged = computed(
  () => storageCapInput.value !== null && storageCapInput.value !== storageCap.value
)
const computeCapValid = computed(() => computeCapInput.value !== null && computeCapInput.value > 0)
const storageCapValid = computed(() => storageCapInput.value !== null && storageCapInput.value > 0)

const computeProgressColor = computed(() =>
  usageColor(computeUsagePercent.value, computeOverLimit.value)
)
const storageProgressColor = computed(() =>
  usageColor(storageUsagePercent.value, storageOverLimit.value)
)

const progressValue = (value: number) => Math.min(Math.max(value, 0), 100)

const usageColor = (usagePercent: number, overLimit: boolean) => {
  if (overLimit) return 'error'
  if (usagePercent >= 80) return 'warning'
  return 'primary'
}

const spendingRows = computed(() => [
  {
    key: 'compute',
    title: 'Estimated calculate costs',
    subtitle: 'One-time',
    estimate: computeCost.value,
    cap: computeCap.value,
    usagePercent: computeUsagePercent.value,
    progressValue: computeProgressValue.value,
    color: computeProgressColor.value,
    overLimit: computeOverLimit.value,
    capInput: computeCapInput.value,
    changed: computeCapChanged.value,
    valid: computeCapValid.value,
    storageLabel: ''
  },
  {
    key: 'storage',
    title: 'Storage costs',
    subtitle: 'Monthly',
    estimate: storageCost.value,
    cap: storageCap.value,
    usagePercent: storageUsagePercent.value,
    progressValue: storageProgressValue.value,
    color: storageProgressColor.value,
    overLimit: storageOverLimit.value,
    capInput: storageCapInput.value,
    changed: storageCapChanged.value,
    valid: storageCapValid.value,
    storageLabel: `${formatStorage(props.spending?.storage_size_gb)} stored`
  }
])

const inputModel = (key: string) => ({
  get: () => (key === 'compute' ? computeCapInput.value : storageCapInput.value),
  set: (value: number | null) => {
    if (key === 'compute') {
      computeCapInput.value = value
    } else {
      storageCapInput.value = value
    }
  }
})

const formatMoney = (value?: number | null) => {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return '-'
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: currency.value,
    maximumFractionDigits: 2
  }).format(Number(value))
}

const formatStorage = (value?: number | null) => {
  if (!value) return '0 GB'
  return `${new Intl.NumberFormat(undefined, { maximumFractionDigits: 2 }).format(value)} GB`
}

const saveCap = (key: string) => {
  if (key === 'compute') {
    if (!computeCapValid.value || computeCapInput.value === null) return
    emit('update-cap', { compute_spending_cap_eur: computeCapInput.value })
    return
  }

  if (!storageCapValid.value || storageCapInput.value === null) return
  emit('update-cap', { storage_spending_cap_eur: storageCapInput.value })
}

watch(
  computeCap,
  (value) => {
    computeCapInput.value = value
  },
  { immediate: true }
)

watch(
  storageCap,
  (value) => {
    storageCapInput.value = value
  },
  { immediate: true }
)
</script>

<template>
  <v-sheet class="project-spending-summary mb-5 px-4 py-3" border rounded>
    <div class="summary-grid">
      <div
        v-for="row in spendingRows"
        :key="row.key"
        class="spending-row"
        :class="{ 'spending-row--readonly': !canIncreaseCap }"
      >
        <div class="spending-copy">
          <div class="text-subtitle-2">{{ row.title }}</div>
          <div class="text-body-2 text-medium-emphasis">
            {{ formatMoney(row.estimate) }} of {{ formatMoney(row.cap) }}
          </div>
        </div>

        <div class="progress-cell">
          <div class="d-flex justify-space-between text-caption text-medium-emphasis mb-1">
            <span>{{ row.subtitle }} - {{ row.usagePercent.toFixed(1) }}%</span>
            <span>{{ row.storageLabel }}</span>
          </div>
          <v-progress-linear
            :color="row.color"
            height="10"
            :indeterminate="loading"
            :model-value="row.progressValue"
            rounded
          />
        </div>

        <div v-if="canIncreaseCap" class="cap-cell">
          <v-text-field
            :model-value="row.capInput"
            density="compact"
            hide-details
            label="Cap"
            min="0.01"
            prefix="EUR"
            type="number"
            variant="outlined"
            @update:model-value="inputModel(row.key).set(Number($event))"
          />
          <v-btn
            color="primary"
            icon="mdi-content-save-outline"
            :disabled="!row.changed || !row.valid"
            :loading="writeLoading"
            size="small"
            variant="text"
            @click="saveCap(row.key)"
          />
        </div>
      </div>
    </div>

    <v-alert
      v-if="computeOverLimit || storageOverLimit"
      class="mt-3"
      color="error"
      density="compact"
      variant="tonal"
    >
      One or more project spending caps are exceeded. Active calculations will be cancelled and
      their compute resources deleted.
    </v-alert>
  </v-sheet>
</template>

<style scoped>
.project-spending-summary {
  background: rgb(var(--v-theme-surface));
}

.summary-grid {
  display: grid;
  gap: 12px;
}

.spending-row {
  align-items: center;
  display: grid;
  gap: 16px;
  grid-template-columns: minmax(220px, 280px) minmax(220px, 1fr) minmax(160px, 240px);
}

.spending-row--readonly {
  grid-template-columns: minmax(220px, 280px) minmax(220px, 1fr);
}

.spending-copy {
  min-width: 0;
}

.cap-cell {
  align-items: center;
  display: flex;
  gap: 8px;
  justify-content: flex-end;
}

.progress-cell {
  min-width: 0;
}

@media (max-width: 840px) {
  .spending-row {
    grid-template-columns: 1fr;
  }

  .cap-cell {
    justify-content: flex-start;
  }
}
</style>
